import { api } from '@/global/services/api'
import L from 'leaflet'
import { cloneDeep } from 'lodash'
import { dateService, FORMATS } from '@/global/services/date'

const ICON_SIZE = {
  iconSize: [30, 30],
  iconAnchor: [15, 33]
}
const RESIZE_TIMEOUT = 300
// How close the map goes to a picked marker, and how much room is left around a group
// of them.
const SELECTED_MARKER_ZOOM = 14
const SELECTED_MARKERS_PADDING = [40, 40]

export default {
  name: 'IndexTableLeafletMapHelper',

  data () {
    return {
      leafletMap: null,
      showLoader: null,
      // The markers wearing the mark right now - see `showSelectedMarkers`.
      selectedMarkerIds: [],
      queryParams: {},
      resizeTimeout: null
    }
  },

  methods: {
    async mapInitialized (event, fetchMarkers = true) {
      // get map and query params from config in parent class
      const { map, queryParams } = event || {}

      // skip if map is undefined
      if (!map) {
        return
      }

      this.queryParams = queryParams || {}
      this.leafletMap = map

      if (!fetchMarkers) {
        await this.fetchPolylines()
      }
    },

    invalidateMapSize () {
      clearTimeout(this.resizeTimeout)
      const that = this
      this.resizeTimeout = setTimeout(() => {
        that.leafletMap.onMapReady(function (map) {
          map?.invalidateSize()
        })
      }, RESIZE_TIMEOUT)
    },

    async fetchMarkers (event) {
      try {
        // skip if leaflet map is undefined
        if (!this.leafletMap) {
          return
        }

        this.showLoader = true
        const { filters } = event || {}
        const modifiedFilters = cloneDeep(filters)
        if (modifiedFilters?.date_time_from) {
          modifiedFilters.date_time_from = dateService.formatCustom(filters?.date_time_from, FORMATS.datetimeShort, FORMATS.isoDatetime)
        }
        if (modifiedFilters?.date_time_to) {
          modifiedFilters.date_time_to = dateService.formatCustom(filters?.date_time_to, FORMATS.datetimeShort, FORMATS.isoDatetime)
        }

        const { data } = await api('road-maintenance').get('traffic-regulation/icons', {
          ...this.queryParams,
          filters: modifiedFilters || {}
        })

        if (data?.length) {
          const markersConfig = {
            markers: data,
            markerClusterOptions: {
              maxClusterRadius: 40
            },
            fitMarkers: true,
            fitBoundsOptions: {
              pad: 0.5
            }
          }

          markersConfig.markers.forEach(marker => {
            const { icon } = marker || {}
            // add marker popup
            marker.popup = {
              popupOptions: {
                autoPan: false,
                offset: [-5, -10]
              },
              popupData: async () => await this.getMarkersTooltipData(marker)
            }
            // add marker click event
            marker.click = () => this.onMarkerClick(marker)
            // add marker icon
            marker.icon = () => {
              return L.divIcon({
                ...ICON_SIZE,
                className: 'orange_icon',
                html: `<span style="background-color: ${icon}" class="custom-marker" />`
              })
            }
          })

          this.leafletMap?.onMapReady((map) => {
            map.generateMarkers(markersConfig)

            // Markers are drawn afresh, so whatever the table still has picked has to
            // be marked again - a refetch is not the user changing their mind. The map
            // is left where it is: it is the rows that moved it, and they have not.
            this.selectedMarkerIds.forEach(markerId => this.setMarkerSelected(markerId, true))
          })
        }
        // remove markers from map
        else {
          this.leafletMap?.onMapReady(function (map) {
            map.removeMarkers()
          })
        }
      }
      catch (exception) {
        console.error(exception)
      }
      finally {
        this.showLoader = false
      }
    },

    async fetchPolylines () {
      try {
        // skip if map is undefined
        if (!this.leafletMap) {
          return
        }

        this.showLoader = true
        const { data } = await api('road-maintenance').get('traffic-activity-feed/polylines', this.queryParams)

        // skip if no polylines received from backend
        if (!data?.length) {
          return
        }

        const polylineConfig = {
          polylines: [],
          markers: [],
          fitPolylines: true
        }

        data.forEach(polylineObj => {
          const { id, color, polyline: polylineLatLngs } = polylineObj || {}

          // skip if polylines is invalid or id is not provided
          if (!polylineLatLngs?.length || !id) {
            return
          }

          const mappedPolylineLatLngs = polylineLatLngs.map(p => [p.lat, p.lng]).filter(polyline => polyline.every(poly => poly))
          // set polyline config
          polylineConfig.polylines.push({
            coordinates: mappedPolylineLatLngs,
            options: {
              id,
              color: color || '#3388ff', // apply polyline color
              weight: 6 // polyline weight
            },
            polylineClick: () => this.handlePolylineClick(polylineObj),
            polylineDecoratorClick: () => this.handlePolylineClick(polylineObj),
            popup: {
              popupData: () => this.getPolylinePopupData(polylineObj)
            }
          })
        })

        // generate polylines on map when map is ready
        this.leafletMap.onMapReady(function (map) {
          map.generatePolylines(polylineConfig)
        })
      }
      catch (exception) {
        console.error(exception)
      }
      finally {
        this.showLoader = false
      }
    },

    getPolylinePopupData (polyline) {
      const { street, start_date_time: startDateTime, end_date_time: endDateTime } = polyline || {}
      const streetLabelStyle = 'text-align: center; font-weight: 700;'
      const datetimeLabelStyle = 'font-weight: 700;'
      const datetimeDivStyle = 'text-align: left;'
      const mainDivStyle = 'font-family: "Satoshi", sans-serif; font-size: 14px; font-weight: 500; letter-spacing: 0; line-height: 20px; color: #111827;'
      const defaultEmptyValue = '--'
      // The endpoint reports these the way it reports them everywhere - the listing
      // reads the same two columns and shows them the same way.
      const formatDateTime = (value) => value
        ? dateService.formatCustom(value, FORMATS.isoDatetime, FORMATS.datetimeShort)
        : defaultEmptyValue

      return '<div style="' + mainDivStyle + '">' +
        '<div style="' + streetLabelStyle + '">' +
          (street || defaultEmptyValue) +
        '</div>' +
        '<div style="' + datetimeDivStyle + '">' +
          '<span style="' + datetimeLabelStyle + '">' +
            this.$t('road-maintenance/aggregator.road_closure_start_time') + ': ' +
          '</span>' +
          '<span>' +
            formatDateTime(startDateTime) +
          '</span>' +
        '</span>' +
        '<div style="' + datetimeDivStyle + '">' +
          '<span style="' + datetimeLabelStyle + '">' +
            this.$t('road-maintenance/aggregator.road_closure_end_time') + ': ' +
          '</span>' +
          '<span>' +
            formatDateTime(endDateTime) +
          '</span>' +
        '</div>' +
      '</div>'
    },

    /**
     * Marks a marker as the picked one, or takes the mark off it.
     *
     * The icon is rebuilt from the one the marker already wears rather than from
     * scratch: its colour is an inline style on the span (see `fetchMarkers`) and its
     * size and anchor are options of the icon, and an icon built without them is the
     * grey, misplaced pin that a picked marker used to turn into. Only the class list
     * of the span changes.
     */
    setMarkerSelected (markerId, isSelected) {
      if (!markerId || !this.leafletMap) {
        return
      }

      // Out of the clustering while it is the picked one: a marker inside a cluster is
      // not on the map at all, and the point of picking a row is to see its marker.
      const marker = this.leafletMap.setMarkerAlwaysVisible?.(markerId, isSelected) ??
        this.leafletMap.findMarkerById(markerId)
      const iconOptions = marker?.getIcon?.()?.options

      if (!iconOptions?.html) {
        return
      }

      const html = iconOptions.html.replace(/class="([^"]*)"/, (match, classList) => {
        const classes = classList
          .split(/\s+/)
          .filter(className => className && className !== 'selected')

        if (isSelected) {
          classes.push('selected')
        }

        return `class="${classes.join(' ')}"`
      })

      if (html === iconOptions.html) {
        return
      }

      marker.setIcon(L.divIcon({
        ...iconOptions,
        html
      }))
    },

    /**
     * Shows on the map what the table has picked out.
     *
     * Every picked row's marker is marked and the map is moved to hold all of them at
     * once - two requests a street apart are both worth seeing, and a map zoomed to
     * the last one clicked hides the rest. A single pick is zoomed to, there being
     * nothing to fit it against.
     */
    showSelectedMarkers (event) {
      try {
        const { data } = event || {}

        if (!this.leafletMap) {
          return
        }

        const selectedIds = (Array.isArray(data) ? data : [])
          .map(row => row?.id)
          .filter(Boolean)

        // Rows unpicked since the last change give their marks up.
        this.selectedMarkerIds
          .filter(markerId => !selectedIds.includes(markerId))
          .forEach(markerId => this.setMarkerSelected(markerId, false))

        selectedIds.forEach(markerId => this.setMarkerSelected(markerId, true))

        this.selectedMarkerIds = selectedIds

        this.frameSelectedMarkers(selectedIds)
      }
      catch (exception) {
        console.error(exception)
      }
    },

    // Moves the map to where the picked markers are: onto the one, or around them all.
    frameSelectedMarkers (markerIds = []) {
      const map = this.leafletMap?.$refs?.map?.mapObject

      if (!map) {
        return
      }

      const positions = markerIds
        .map(markerId => this.leafletMap.findMarkerById(markerId)?._latlng)
        .filter(position => position?.lat && position?.lng)
        .map(({ lat, lng }) => L.latLng([lat, lng]))

      if (!positions.length) {
        return
      }

      if (positions.length === 1) {
        map.setView(positions[0], SELECTED_MARKER_ZOOM)

        return
      }

      // Never closer than a single pick would go, so a handful of markers in the same
      // street does not leave the map zoomed into the pavement between them.
      map.fitBounds(L.latLngBounds(positions), {
        padding: SELECTED_MARKERS_PADDING,
        maxZoom: SELECTED_MARKER_ZOOM
      })
    },

    async getMarkersTooltipData (marker) {
      const { validity, address } = marker || {}

      return '<div> ' +
        '<span><b>' + this.$t('road-maintenance/patrol-service.address') + ':</b> ' + (address || '') + '</span>' +
        '<br>' +
        '<span><b>' + this.$t('road-maintenance.date') + ':</b> ' + (validity || '') + '</span>' +
        '</div>'
    }
  }
}
